// /parent — layout route (PR-PARENT-OVERVIEW). Lifts the ParentGate from
// inside ParentSettings to wrap the ENTIRE /parent area, so kids can't reach
// the dashboard (or any parent surface) without clearing the gate. Gate
// BEHAVIOR is unchanged (same parentGate sessionStorage unlock) — only WHERE
// it's applied moved up one level. Cancel leaves the parent area for the kid
// view (/play), since there's no longer a parent screen behind the gate.
//
// MA-03: personal-device bypass — if deviceMode=personal the device IS the
// parent's own phone, so the kid-lock is unnecessary. Gate is auto-passed,
// preserving the unlockParentGate sessionStorage contract so any code that
// checks isParentGateUnlocked() still sees a live session.
import { useEffect, useState } from "react";
import { createFileRoute, Outlet, useNavigate } from "@tanstack/react-router";
import { isParentGateUnlocked, onHandoffArmedElsewhere, personalBypassAllowed, unlockParentGate } from "@/lib/parentGate";
import { getDeviceMode } from "@/lib/deviceMode";
import { ParentGate } from "@/components/ParentGate";
import { ParentRouteError } from "@/components/RouterErrorFallback";
import { ParentShell } from "@/components/ParentShell";
import { logParentAreaEvent } from "@/lib/parent/parentAreaEvents";

// MA-852 (D-PUX-1): past the gate, every /parent/* screen renders inside the
// one parent shell (shared header + the four-tab bar).
function ParentAreaLayout() {
  const navigate = useNavigate();
  // MA-883 RED r1 M1: decide bypass / already-unlocked synchronously, so the
  // gate never mounts (and never reports gate_shown) on a personal device or
  // in a session that is already unlocked. This also removes the one-frame
  // gate flash those visits used to get. The effect below still stamps the
  // personal-device unlock.
  const [unlocked, setUnlocked] = useState(
    () => personalBypassAllowed(getDeviceMode()) || isParentGateUnlocked(),
  );

  useEffect(() => {
    // MA-855: unless the device was handed to a child ("Hand to kid") and
    // no grown-up has passed the gate since — then it asks, like a kiosk.
    if (personalBypassAllowed(getDeviceMode())) {
      // Parent's own device — skip the kid-lock and stamp the session unlock
      // so any downstream isParentGateUnlocked() check is satisfied.
      unlockParentGate();
      setUnlocked(true);
    } else {
      setUnlocked(isParentGateUnlocked());
    }
  }, []);

  // MA-855 RED r1 SHOULD-1: a "Hand to kid" in another tab closes this one.
  useEffect(() => onHandoffArmedElsewhere(() => setUnlocked(false)), []);

  if (!unlocked) {
    return (
      <ParentGate
        onUnlock={() => setUnlocked(true)}
        onCancel={() => navigate({ to: "/play" })}
        // MA-883: only this gate (in front of /parent) reports; fire-and-forget.
        onGateEvent={(type) => logParentAreaEvent({ type })}
      />
    );
  }

  return (
    <ParentShell>
      <Outlet />
    </ParentShell>
  );
}

export const Route = createFileRoute("/parent")({ component: ParentAreaLayout, errorComponent: ParentRouteError });
